import { retainFailureDiagnostic } from "@/lib/inspections/failure-diagnostic";
import {
  Cause,
  Effect,
  Either,
  Exit,
  FiberRef,
  Option,
  ParseResult,
} from "effect";
import {
  HttpClient,
  type HttpApiError,
  type HttpClientError,
} from "@effect/platform";
import type { Reporter } from "@/lib/inspections/reporter";

/**
 * The failures the boundary handles for every caller: the request did not
 * reach the server, or what came back is not what the contract says.
 */
export type CentralFailure =
  | HttpClientError.HttpClientError
  | HttpApiError.HttpApiDecodeError;

/** The `errorClass` of a failure no retry changes. */
export const CONTRACT_MISMATCH = "ContractMismatch";

/** The `errorClass` of a failure the same request may get past later. */
export const RETRYABLE = "Retryable";

/** The `errorClass` of a request its caller's signal ended. */
export const INTERRUPTED = "Interrupted";

/** 408 and 429 are how a server or proxy says "later"; they are expected. */
const TRANSIENT_CLIENT_STATUSES = new Set([408, 429]);

/** A 4xx that came back without a declared reply: retried, but reported, since it may be a server bug. */
export const isUnexpectedStatus = (status: number | undefined): boolean =>
  status !== undefined &&
  status < 500 &&
  !TRANSIENT_CLIENT_STATUSES.has(status);

/**
 * No declared reply came back: the server could not be reached, could not
 * answer, or something in front of it answered instead. Only a reply that
 * decodes as the contract's is the server's verdict, so the same request may
 * succeed later.
 */
export class Retryable extends Error {
  readonly _tag = "Retryable";
  /** What the sync queue recognises as a write to send again. */
  readonly errorClass = RETRYABLE;
  /** See {@link isUnexpectedStatus}. */
  readonly unexpected: boolean;
  constructor(
    /** Which failure it was, for the breadcrumb. */
    readonly detail: string,
    readonly status?: number,
    options?: { cause: unknown },
    readonly phase?: "response-body",
  ) {
    super(`Request will be retried (${detail})`, options);
    this.unexpected = isUnexpectedStatus(status);
  }
}

/** No retry changes it: the server claimed success in a shape the contract does not have, or the request itself broke the contract. */
export class ContractMismatch extends Error {
  readonly _tag = "ContractMismatch";
  /** What the sync queue groups on and recognises as terminal. */
  readonly errorClass = CONTRACT_MISMATCH;
  constructor(
    /** Which failure it was, for the report. */
    readonly detail: string,
    readonly status?: number,
    options?: { cause: unknown },
  ) {
    super(`Response does not match the contract (${detail})`, options);
  }
}

/** The caller's signal aborted the request. Nothing failed. */
export class Interrupted extends Error {
  readonly _tag = "Interrupted";
  readonly errorClass = INTERRUPTED;
  constructor() {
    super("Request interrupted");
  }
}

/** A gateway or proxy answering for an app that is restarting; expected, so not reported. */
export const GATEWAY_STATUSES = new Set([502, 503, 504]);

const isSuccess = (status: number) => status >= 200 && status < 300;

/** A reply that did not decode as a declared one is only the contract's fault when it claimed success. */
const undecodedReply = (detail: string, status: number, cause: unknown) =>
  isSuccess(status)
    ? new ContractMismatch(detail, status, { cause })
    : new Retryable(detail, status, { cause });

// Status is metadata for the response-decoding hook, not defect provenance.
// Fiber-local storage keeps concurrent calls separate.
const responseTrace = FiberRef.unsafeMake<string | undefined>(undefined);
const responseStatus = FiberRef.unsafeMake<number | undefined>(undefined);

/** Records the reply status for the boundary that runs the typed client call. */
export const withBoundaryResponseStatus = (client: HttpClient.HttpClient) =>
  HttpClient.tap(client, (response) =>
    FiberRef.set(responseStatus, response.status).pipe(
      Effect.zipRight(
        FiberRef.set(
          responseTrace,
          response.headers["x-sync-trace-id"] ??
            response.request.headers["sentry-trace"]?.split("-")[0],
        ),
      ),
    ),
  );

/** Classifies response decoding only; payload construction and caller defects remain unexpected. */
export const withBoundaryResponseDecode = <A, E, R>(
  decode: Effect.Effect<A, E, R>,
) =>
  Effect.catchIf(
    decode,
    (failure: E): failure is E & ParseResult.ParseError =>
      ParseResult.isParseError(failure),
    (failure) =>
      Effect.flatMap(FiberRef.get(responseStatus), (status) =>
        status === undefined
          ? Effect.fail(failure)
          : Effect.die(undecodedReply("ParseError", status, failure)),
      ),
  );

const isCentral = (failure: unknown): failure is CentralFailure =>
  typeof failure === "object" &&
  failure !== null &&
  "_tag" in failure &&
  (failure._tag === "RequestError" ||
    failure._tag === "ResponseError" ||
    failure._tag === "HttpApiDecodeError");

/** Decode also wraps body-read rejections; JSON and schema errors are contract failures. */
const isBodyReadFailure = (cause: unknown): boolean => {
  const error = Cause.isCause(cause) ? Cause.squash(cause) : cause;
  if (typeof error !== "object" || error === null || !("name" in error))
    return false;
  if (error.name === "AbortError" || error.name === "NetworkError") return true;
  return (
    error.name === "TypeError" &&
    "message" in error &&
    typeof error.message === "string" &&
    /^(Failed to fetch|NetworkError when attempting to fetch resource\.|Load failed|fetch failed|network error)$/.test(
      error.message,
    )
  );
};

const classify = (failure: CentralFailure): Retryable | ContractMismatch => {
  const cause = { cause: failure };
  switch (failure._tag) {
    case "RequestError":
      return failure.reason === "Transport"
        ? new Retryable("RequestError", undefined, cause)
        : new ContractMismatch("RequestError", undefined, cause);
    case "ResponseError":
      if (failure.reason === "Decode" && isBodyReadFailure(failure.cause))
        return new Retryable("ResponseBody", undefined, cause, "response-body");
      // An undeclared status, or a body that does not decode: not a verdict
      // from the contract, whoever sent it.
      return undecodedReply("ResponseError", failure.response.status, failure);
    case "HttpApiDecodeError":
      return new ContractMismatch("HttpApiDecodeError", undefined, cause);
  }
};

/** What is left of a call's failures once the central ones are handled. */
export type Declared<E> = Exclude<E, Extract<E, CentralFailure>>;

/** What the boundary makes of a central failure or an aborted request. */
export type BoundaryFailure = Retryable | ContractMismatch | Interrupted;

/**
 * Composes a contract call. Declared failures keep their typed error channel;
 * central failures become BoundaryFailure values. Unexpected defects remain defects.
 */
export type Boundary = <A, E>(
  call: Effect.Effect<A, E>,
) => Effect.Effect<A, Declared<E> | BoundaryFailure>;

/** The same for a result a legacy wrapper has already produced. */
export type ResultBoundary = <A, E>(
  result: Either.Either<A, E>,
) => Either.Either<A, Declared<E> | BoundaryFailure>;

const declaredOnly = <A, E>(
  call: Effect.Effect<A, E>,
): Effect.Effect<A, Declared<E>> =>
  Effect.catchIf(
    call,
    (failure: E): failure is Extract<E, CentralFailure> => isCentral(failure),
    (failure) => Effect.die(classify(failure)),
  );

/**
 * The `Either` of an exit. A central failure becomes its `BoundaryFailure`,
 * reported by its owner; an unexpected defect is captured by its owner and rejects.
 */
function settle<A, E>(
  exit: Exit.Exit<A, E>,
  reporter: Reporter,
  reporting: "boundary" | "caller",
): Either.Either<A, E | BoundaryFailure> {
  if (Exit.isSuccess(exit)) return Either.right(exit.value);
  if (Cause.isInterruptedOnly(exit.cause))
    return Either.left(new Interrupted());
  const failure = Cause.failureOption(exit.cause);
  if (Option.isSome(failure)) return Either.left(failure.value);
  const defect = Cause.squash(exit.cause);
  if (defect instanceof ContractMismatch) {
    if (reporting === "boundary") {
      reporter.captureMessage(`Contract mismatch: ${defect.detail}`, {
        level: "error",
        tags: { area: "api-boundary", detail: defect.detail },
        extra: { status: defect.status },
      });
    } else {
      reporter.addBreadcrumb({
        category: "api-boundary",
        level: "error",
        message: `Contract mismatch: ${defect.detail}`,
        data: { status: defect.status },
      });
    }
    return Either.left(defect);
  }
  if (defect instanceof Retryable && defect.unexpected) {
    if (reporting === "boundary") {
      reporter.captureMessage(`Unexpected response: ${defect.detail}`, {
        level: "warning",
        tags: { area: "api-boundary", detail: defect.detail },
        extra: { status: defect.status },
      });
    } else {
      reporter.addBreadcrumb({
        category: "api-boundary",
        level: "warning",
        message: `Unexpected response: ${defect.detail}`,
        data: { status: defect.status },
      });
    }
    return Either.left(defect);
  }
  if (defect instanceof Retryable) {
    reporter.addBreadcrumb({
      category: "api-boundary",
      level: "warning",
      message: `request will be retried (${defect.detail})`,
      data: {
        status: defect.status,
        ...(defect.phase ? { phase: defect.phase } : {}),
      },
    });
    return Either.left(defect);
  }
  if (reporting === "boundary")
    reporter.captureException(defect, { tags: { area: "api-boundary" } });
  throw defect;
}

/** Handles the central failures of an already-run result exactly as `createBoundary` does. */
export function createResultBoundary({
  reporter,
  reporting = "boundary",
}: {
  reporter: Reporter;
  /** The boundary captures by default; queued calls leave capture ownership to their caller. */
  reporting?: "boundary" | "caller";
}): ResultBoundary {
  return <A, E>(result: Either.Either<A, E>) =>
    settle(Effect.runSyncExit(declaredOnly(result)), reporter, reporting);
}

/** Retain metadata while the request fiber is still alive, before any Promise/Either adapter exits. */
export const withRequestDiagnostics = <A, E, R>(
  call: Effect.Effect<A, E, R>,
): Effect.Effect<A, E, R> =>
  call.pipe(
    Effect.onExit((exit) =>
      Effect.gen(function* () {
        if (Exit.isFailure(exit)) {
          const traceId = yield* FiberRef.get(responseTrace);
          const status = yield* FiberRef.get(responseStatus);
          retainFailureDiagnostic(Cause.squash(exit.cause), {
            ...(traceId && { traceId }),
            ...(status && { status }),
          });
        }
      }),
    ),
    Effect.locally(responseTrace, undefined),
    Effect.locally(responseStatus, undefined),
  );

/** Composes a typed request without starting a runtime or converting it to a Promise. */
export function createBoundary({
  reporter,
  reporting = "boundary",
}: {
  reporter: Reporter;
  reporting?: "boundary" | "caller";
}) {
  return <A, E>(
    call: Effect.Effect<A, E>,
  ): Effect.Effect<A, Declared<E> | BoundaryFailure> =>
    declaredOnly(call).pipe(
      withRequestDiagnostics,
      Effect.exit,
      Effect.flatMap((exit) =>
        Effect.suspend(() => settle(exit, reporter, reporting)),
      ),
    );
}
